<script setup>
import { ref, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import ContactAPI from 'dashboard/api/contacts';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['added']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const column = ref(null);
const mode = ref('search'); // 'search' | 'create'
const isBusy = ref(false);

const query = ref('');
const results = ref([]);
const isSearching = ref(false);
let searchTimer = null;

const form = reactive({ name: '', email: '', phoneNumber: '' });

const resetState = () => {
  mode.value = 'search';
  query.value = '';
  results.value = [];
  form.name = '';
  form.email = '';
  form.phoneNumber = '';
};

const openForColumn = col => {
  column.value = col;
  resetState();
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

const runSearch = () => {
  clearTimeout(searchTimer);
  const term = query.value.trim();
  if (!term) {
    results.value = [];
    return;
  }
  searchTimer = setTimeout(async () => {
    isSearching.value = true;
    try {
      const { data } = await ContactAPI.search(term, 1, 'name', '');
      results.value = data.payload || [];
    } catch {
      results.value = [];
    } finally {
      isSearching.value = false;
    }
  }, 300);
};

// Assign the column's label to the contact so it appears as a card here.
const addContactToColumn = async contact => {
  isBusy.value = true;
  try {
    const { data } = await ContactAPI.getContactLabels(contact.id);
    const next = [...new Set([...(data.payload || []), column.value.labelTitle])];
    await ContactAPI.updateContactLabels(contact.id, next);
    emit('added', column.value.id);
    useAlert(t('KANBAN.ADD_CARD.ADDED', { name: contact.name || contact.email || '' }));
    close();
  } catch {
    useAlert(t('KANBAN.ADD_CARD.ERROR'));
  } finally {
    isBusy.value = false;
  }
};

const createAndAdd = async () => {
  if (!form.name.trim()) return;
  isBusy.value = true;
  try {
    const contact = await store.dispatch('contacts/create', {
      name: form.name.trim(),
      email: form.email.trim() || undefined,
      phoneNumber: form.phoneNumber.trim() || undefined,
    });
    if (!contact?.id) throw new Error('create failed');
    await addContactToColumn(contact);
  } catch {
    useAlert(t('KANBAN.ADD_CARD.ERROR'));
  } finally {
    isBusy.value = false;
  }
};

defineExpose({ openForColumn, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('KANBAN.ADD_CARD.TITLE')"
    :show-confirm-button="false"
    :cancel-button-label="t('KANBAN.ADD_CARD.CANCEL')"
  >
    <div class="flex flex-col gap-4">
      <p v-if="column && !column.labelTitle" class="text-sm text-n-ruby-11">
        {{ t('KANBAN.ADD_CARD.NEEDS_LABEL') }}
      </p>

      <template v-else>
        <!-- Mode switch -->
        <div class="flex gap-1 p-1 rounded-lg bg-n-alpha-black2 w-fit">
          <button
            v-for="m in ['search', 'create']"
            :key="m"
            type="button"
            class="px-3 py-1 text-xs rounded-md transition-colors"
            :class="
              mode === m
                ? 'bg-n-solid-1 text-n-slate-12 font-medium'
                : 'text-n-slate-11'
            "
            @click="mode = m"
          >
            {{ t(`KANBAN.ADD_CARD.MODE.${m.toUpperCase()}`) }}
          </button>
        </div>

        <!-- Search existing -->
        <div v-if="mode === 'search'" class="flex flex-col gap-2">
          <Input
            v-model="query"
            :placeholder="t('KANBAN.ADD_CARD.SEARCH_PLACEHOLDER')"
            @input="runSearch"
          />
          <div class="flex flex-col max-h-64 gap-1 overflow-y-auto">
            <button
              v-for="c in results"
              :key="c.id"
              type="button"
              :disabled="isBusy"
              class="flex flex-col items-start px-3 py-2 text-left rounded-lg hover:bg-n-alpha-1 disabled:opacity-50"
              @click="addContactToColumn(c)"
            >
              <span class="text-sm text-n-slate-12">{{ c.name || '—' }}</span>
              <span v-if="c.email || c.phone_number" class="text-xs text-n-slate-10">
                {{ c.email || c.phone_number }}
              </span>
            </button>
            <span
              v-if="query.trim() && !results.length && !isSearching"
              class="px-1 py-2 text-xs text-n-slate-10"
            >
              {{ t('KANBAN.ADD_CARD.NO_RESULTS') }}
            </span>
          </div>
        </div>

        <!-- Create new -->
        <form v-else class="flex flex-col gap-3" @submit.prevent="createAndAdd">
          <Input v-model="form.name" :label="t('KANBAN.ADD_CARD.NAME')" />
          <Input v-model="form.email" :label="t('KANBAN.ADD_CARD.EMAIL')" />
          <Input v-model="form.phoneNumber" :label="t('KANBAN.ADD_CARD.PHONE')" />
          <Button
            type="submit"
            color="blue"
            :label="t('KANBAN.ADD_CARD.CREATE')"
            :disabled="!form.name.trim() || isBusy"
            :is-loading="isBusy"
          />
        </form>
      </template>
    </div>
  </Dialog>
</template>
